/*
 * magazin-events.js: "Nächste Events" under the blog posts (snippets/magazin-events.liquid).
 *
 * Same data path as the archive on the events page (events-archive.js): the section renders
 * one batch of store pages into <template data-events-batch>, every page on the event-detail
 * template with its first and last day. Liquid paginates `pages` by at most 50 and the store
 * sorts them by handle, so the soonest event can sit in any batch. Once the element nears the
 * viewport it fetches the other batches through the Section Rendering API, keeps the events
 * whose last day is today or later, sorts them by first day and shows the first `data-limit`.
 *
 * "Today" is the visitor's date in the shop's time zone, not the server's, so a render cached
 * past midnight still drops yesterday's event. A failed batch leaves the list empty: the link
 * to the events page above it still works, and "no events" would be a false claim.
 */

const MAGAZIN_EVENTS_TIME_ZONE = 'Europe/Berlin';

/* en-CA formats as YYYY-MM-DD, the same shape as the data attributes, so dates compare
   as strings. */
function magazinEventsToday(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: MAGAZIN_EVENTS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

class MagazinEvents extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.list = this.querySelector('[data-magazin-events-list]');
    this.empty = this.querySelector('[data-magazin-events-empty]');
    this.limit = Number(this.dataset.limit) || 3;
    if (!this.list) return;

    if (!('IntersectionObserver' in window)) {
      this.show();
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.show();
      },
      { rootMargin: '600px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  batchUrl(page) {
    return `${window.location.pathname}?section_id=${encodeURIComponent(this.dataset.sectionId)}&page=${page}`;
  }

  async fetchBatch(page) {
    const response = await fetch(this.batchUrl(page));
    if (!response.ok) throw new Error(`magazin events: batch ${page} answered ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const template = doc.querySelector('template[data-events-batch]');
    if (!template) throw new Error(`magazin events: batch ${page} has no event markup`);
    return template;
  }

  async load() {
    const own = this.querySelector('template[data-events-batch]');
    const pages = Number(own && own.dataset.pages) || 1;
    const current = Number(own && own.dataset.page) || 1;

    const others = [];
    for (let page = 1; page <= pages; page += 1) {
      if (page !== current) others.push(page);
    }
    const batches = await Promise.all(others.map((page) => this.fetchBatch(page)));

    const today = magazinEventsToday();
    const seen = new Set();
    const items = [];
    for (const template of [own, ...batches]) {
      if (!template) continue;
      for (const item of template.content.children) {
        const key = item.dataset.eventUrl;
        const end = item.dataset.eventEnd || item.dataset.eventStart;
        if (!end || end < today || seen.has(key)) continue;
        seen.add(key);
        items.push(item);
      }
    }

    /* Soonest first: by first day, then by last day. */
    return items
      .sort(
        (a, b) =>
          (a.dataset.eventStart || '').localeCompare(b.dataset.eventStart || '') ||
          (a.dataset.eventEnd || '').localeCompare(b.dataset.eventEnd || '')
      )
      .slice(0, this.limit);
  }

  async show() {
    let items;
    try {
      items = await this.load();
    } catch (error) {
      console.error(error);
      return;
    }

    for (const item of items) this.list.append(document.importNode(item, true));
    if (items.length === 0 && this.empty) this.empty.hidden = false;
  }
}

if (!customElements.get('magazin-events')) {
  customElements.define('magazin-events', MagazinEvents);
}
